/**
 * Shortcuts popup - lists all keybindings (Item 10c).
 *
 * Opened from the "shortcuts" link in the status bar footer. Styled to match
 * the settings popup for visual consistency.
 */

import { t } from '../i18n';
import {
  KEYBOARD_SHORTCUTS,
  KEYBOARD_SHORTCUT_GROUPS,
  KEYBOARD_SHORTCUT_GROUP_LABEL_KEYS,
  type KeyboardShortcutGroup,
} from './keyboard-shortcuts';

let closePopup: (() => void) | null = null;

/** Open the shortcuts popup anchored above the given trigger element. */
export function openShortcutsPopup(anchor: HTMLElement): void {
  // Close any existing popup first.
  closePopup?.();

  const popup = document.createElement('div');
  popup.className = 'settings-popup shortcuts-popup';
  popup.setAttribute('role', 'dialog');
  popup.setAttribute('aria-modal', 'true');
  popup.setAttribute('aria-label', t('shortcuts.title'));

  const title = document.createElement('div');
  title.className = 'settings-popup-label';
  title.textContent = t('shortcuts.title');
  popup.appendChild(title);

  const list = document.createElement('ul');
  list.className = 'shortcuts-list';
  for (const groupKey of KEYBOARD_SHORTCUT_GROUPS) {
    const items = KEYBOARD_SHORTCUTS.filter(
      (s): s is (typeof KEYBOARD_SHORTCUTS)[number] & { group: KeyboardShortcutGroup } =>
        s.group === groupKey,
    );
    if (items.length === 0) continue;

    const head = document.createElement('li');
    head.className = 'shortcuts-group-head';
    head.setAttribute('role', 'presentation');
    head.textContent = t(KEYBOARD_SHORTCUT_GROUP_LABEL_KEYS[groupKey]);
    list.appendChild(head);

    for (const shortcut of items) {
      const li = document.createElement('li');
      li.className = 'shortcuts-row';
      const kbd = document.createElement('span');
      kbd.className = 'kbd';
      kbd.textContent = shortcut.keys;
      const desc = document.createElement('span');
      desc.textContent = t(shortcut.descriptionKey);
      li.appendChild(kbd);
      li.appendChild(desc);
      list.appendChild(li);
    }
  }
  popup.appendChild(list);

  // Position above the anchor.
  document.body.appendChild(popup);
  const anchorRect = anchor.getBoundingClientRect();
  const popupRect = popup.getBoundingClientRect();
  popup.style.cssText = [
    'position:fixed',
    `left:${anchorRect.right - popupRect.width}px`,
    `top:${anchorRect.top - popupRect.height - 4}px`,
    'z-index:9000',
  ].join(';');

  function close(): void {
    if (closePopup !== close) return;
    closePopup = null;
    document.removeEventListener('click', handleOutside, true);
    document.removeEventListener('keydown', handleKey, true);
    popup.remove();
  }

  function handleOutside(event: MouseEvent): void {
    if (!popup.contains(event.target as Node) && event.target !== anchor) {
      close();
    }
  }

  function handleKey(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.stopPropagation();
      close();
    }
  }

  // Delay to avoid immediate close from the triggering click.
  setTimeout(() => {
    document.addEventListener('click', handleOutside, true);
    document.addEventListener('keydown', handleKey, true);
  }, 0);

  closePopup = close;
}
